import { useState, useEffect } from 'react';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Truck, ArrowUp, ArrowDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const PAYMENT_METHODS = ['当日現金', '翌日現金', '翌週掛け', '月掛け', '月掛け現金', '15日掛け'];

const initialFormData = {
  code: '',
  name: '',
  contact_person: '',
  email: '',
  phone: '',
  fax: '',
  address: '',
  notes: '',
  payment_method: '',
  order_method: 'FAX',
  delivery_type: '',
};

export const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getSuppliers();
      setSuppliers(res.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 並び替え（上下移動）
  const moveItem = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= suppliers.length) return;
    
    const newSuppliers = [...suppliers];
    [newSuppliers[index], newSuppliers[newIndex]] = [newSuppliers[newIndex], newSuppliers[index]];
    setSuppliers(newSuppliers);
    
    // sort_orderを更新
    try {
      const updates = newSuppliers.map((sup, idx) => 
        updateSupplier(sup.id, { sort_order: idx })
      );
      await Promise.all(updates);
      toast.success('並び順を更新しました');
    } catch (error) {
      toast.error('並び替えの保存に失敗しました');
      fetchData();
    }
  };

  const handleAdd = () => {
    setSelectedSupplier(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      code: supplier.code,
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      fax: supplier.fax || '',
      address: supplier.address || '',
      notes: supplier.notes || '',
      payment_method: supplier.payment_method || '',
      order_method: supplier.order_method ?? 'FAX',
      delivery_type: supplier.delivery_type || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = (supplier) => {
    setSelectedSupplier(supplier);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('必須項目を入力してください');
      return;
    }

    setSaving(true);
    try {
      if (selectedSupplier) {
        await updateSupplier(selectedSupplier.id, formData);
        toast.success('仕入れ先を更新しました');
      } else {
        await createSupplier(formData);
        toast.success('仕入れ先を登録しました');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'エラーが発生しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteSupplier(selectedSupplier.id);
      toast.success('仕入れ先を削除しました');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="suppliers-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">仕入れ先</h1>
        <Button onClick={handleAdd} data-testid="add-supplier-btn">
          <Plus className="h-4 w-4 mr-2" />
          新規登録
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="仕入れ先名・コードで検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredSuppliers.length === 0 ? (
            <div className="empty-state py-12">
              <Truck className="h-12 w-12 text-slate-300 mb-3" />
              <p>仕入れ先がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="suppliers-table">
                <thead>
                  <tr>
                    <th className="w-20">順序</th>
                    <th>コード</th>
                    <th>仕入れ先名</th>
                    <th>担当者</th>
                    <th>メール</th>
                    <th>電話番号</th>
                    <th>支払方法</th>
                    <th>発注手段</th>
                    <th className="text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier, index) => (
                    <tr key={supplier.id}>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveItem(index, 'up')} disabled={index === 0 || searchTerm}>
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveItem(index, 'down')} disabled={index === suppliers.length - 1 || searchTerm}>
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="font-mono">{supplier.code}</td>
                      <td className="font-medium">{supplier.name}</td>
                      <td>{supplier.contact_person || '-'}</td>
                      <td>{supplier.email || '-'}</td>
                      <td>{supplier.phone || '-'}</td>
                      <td>{supplier.payment_method || '-'}</td>
                      <td>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${supplier.order_method === 'LINE' ? 'bg-green-100 text-green-700' : supplier.order_method === 'FAX' ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'}`}>
                          {supplier.order_method || '送信なし'}
                        </span>
                      </td>
                      <td className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(supplier)}
                          data-testid={`edit-supplier-${supplier.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(supplier)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-supplier-${supplier.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{selectedSupplier ? '仕入れ先編集' : '仕入れ先登録'}</DialogTitle>
          </DialogHeader>
          <div className="form-grid py-4">
            <div className="space-y-2">
              <Label>仕入れ先コード *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="SUPP-001"
                data-testid="supplier-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>仕入れ先名 *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="株式会社サプライヤー"
                data-testid="supplier-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>担当者</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="佐藤 花子"
                data-testid="supplier-contact-input"
              />
            </div>
            <div className="space-y-2">
              <Label>メールアドレス</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="supplier@example.com"
                data-testid="supplier-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>電話番号</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="03-9876-5432"
                data-testid="supplier-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>FAX番号</Label>
              <Input
                value={formData.fax}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                placeholder="03-9876-5433"
                data-testid="supplier-fax-input"
              />
            </div>
            <div className="space-y-2">
              <Label>住所</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="大阪府大阪市..."
                data-testid="supplier-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>支払方法</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
              >
                <SelectTrigger data-testid="supplier-payment-method">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>発注手段</Label>
              <div className="flex gap-2" data-testid="supplier-order-method">
                {['FAX', 'LINE', '送信なし'].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setFormData({ ...formData, order_method: m === '送信なし' ? '' : m })}
                    className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      (m === '送信なし' ? !formData.order_method : formData.order_method === m)
                        ? m === 'LINE' ? 'border-green-500 bg-green-50 text-green-700' 
                          : m === 'FAX' ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-500 bg-slate-50 text-slate-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>納品形態</Label>
              <div className="flex gap-2">
                {[{v:'引',label:'引き取り'},{v:'届',label:'車に届く'},{v:'買',label:'現場購入'}].map(({v,label}) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFormData({ ...formData, delivery_type: formData.delivery_type === v ? '' : v })}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex-1 ${
                      formData.delivery_type === v ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {v}（{label}）
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>備考</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="メモなど"
                rows={3}
                data-testid="supplier-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-supplier-btn">
              {saving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>仕入れ先を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{selectedSupplier?.name}」を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
